/**
 * Ziel-Route für den "Ausstempeln"-Action-Button aus der Push-Notification.
 *
 * Setzt sofort die Endzeit der angegebenen Schicht auf jetzt und Status auf
 * "done". Zeigt eine kurze Erfolgs-/Fehlermeldung an und leitet dann zur
 * Hauptseite weiter.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUpdateShift } from '../data/shifts';
import { db } from '../db/schema';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { dismissClockedInNotification } from '../lib/clockNotification';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function nowHHMM(): string {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export default function ClockOutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const update = useUpdateShift();
  const [state, setState] = useState<'pending' | 'ok' | 'error' | 'not-found'>('pending');
  const [message, setMessage] = useState<string>('Stempele aus…');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) {
        setState('not-found');
        setMessage('Keine Schicht-ID übergeben.');
        return;
      }
      const shift = await db.shifts.get(id);
      if (!shift) {
        setState('not-found');
        setMessage('Schicht nicht gefunden (vielleicht offline und noch nicht synchronisiert).');
        return;
      }
      if (cancelled) return;
      try {
        await update.mutateAsync({
          id,
          input: { endTime: nowHHMM(), status: 'done' },
        });
        if (cancelled) return;
        void dismissClockedInNotification();
        hapticSuccess();
        setState('ok');
        setMessage(`Ausgestempelt um ${nowHHMM()}.`);
        // Nach 2 Sekunden zurück zur Hauptseite
        setTimeout(() => navigate('/', { replace: true }), 2000);
      } catch (err) {
        if (cancelled) return;
        hapticError();
        setState('error');
        setMessage(err instanceof Error ? err.message : 'Ausstempeln fehlgeschlagen.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="max-w-md mx-auto mt-12 text-center space-y-4">
      <h2 className="text-xl font-semibold">
        {state === 'ok' ? 'Erfolgreich ausgestempelt' : 'Ausstempeln'}
      </h2>
      <p
        className={
          state === 'ok'
            ? 'text-emerald-400'
            : state === 'error' || state === 'not-found'
              ? 'text-red-400'
              : 'text-slate-300'
        }
      >
        {message}
      </p>
      {state !== 'pending' && (
        <button
          onClick={() => navigate('/', { replace: true })}
          className="px-4 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600"
        >
          Zur Übersicht
        </button>
      )}
    </div>
  );
}
